import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Boxes, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import api, { fmt, errText, CATEGORIES, undoToast } from "@/lib/api";

const EMPTY = { design_name: "", category: "Suit", color: "", size: "", quantity: 1, cost_price: 0, selling_price: 0, status: "Available" };
const STATUSES = ["Available", "Reserved", "Sold"];

export default function Stock() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("All");

  const { data: stock, isLoading } = useQuery({ queryKey: ["stock"], queryFn: async () => (await api.get("/stock")).data });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, quantity: Number(form.quantity) || 1, cost_price: Number(form.cost_price) || 0, selling_price: Number(form.selling_price) || 0 };
      return editId ? (await api.put(`/stock/${editId}`, body)).data : (await api.post("/stock", body)).data;
    },
    onSuccess: () => { toast.success(editId ? "Stock updated" : "Stock added"); qc.invalidateQueries({ queryKey: ["stock"] }); close(); },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: async (sid) => (await api.delete(`/stock/${sid}`)).data,
    onSuccess: (data) => { undoToast("Stock deleted", data.undo_id, () => qc.invalidateQueries({ queryKey: ["stock"] })); qc.invalidateQueries({ queryKey: ["stock"] }); },
  });

  const close = () => { setOpen(false); setEditId(null); setForm(EMPTY); };
  const filtered = (stock || []).filter((s) => filter === "All" || s.status === filter);

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Shop Stock</h1>
          <p className="text-sm text-muted-foreground mt-1">{stock?.length || 0} stock items</p>
        </div>
        <Button data-testid="add-stock-button" className="rounded-full active:scale-95 transition-transform" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1.5" /> Add Stock
        </Button>
      </div>

      <div className="flex gap-2">
        {["All", ...STATUSES].map((s) => (
          <button key={s} data-testid={`stock-filter-${s.toLowerCase()}`} onClick={() => setFilter(s)} className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}>
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Boxes className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">No stock items</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Design</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Color / Size</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Selling</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`stock-row-${s.id}`} className="hover:bg-muted/50 transition-colors">
                  <TableCell className="font-medium">{s.design_name}</TableCell>
                  <TableCell>{s.category}</TableCell>
                  <TableCell className="text-muted-foreground">{[s.color, s.size].filter(Boolean).join(" / ") || "—"}</TableCell>
                  <TableCell className="text-right">{s.quantity}</TableCell>
                  <TableCell className="text-right">{fmt(s.cost_price)}</TableCell>
                  <TableCell className="text-right">{fmt(s.selling_price)}</TableCell>
                  <TableCell className={`text-right font-medium ${s.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(s.profit)}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-stock-${s.id}`} onClick={() => { setEditId(s.id); setForm({ design_name: s.design_name, category: s.category, color: s.color, size: s.size, quantity: s.quantity, cost_price: s.cost_price, selling_price: s.selling_price, status: s.status }); setOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" data-testid={`delete-stock-${s.id}`} onClick={() => remove.mutate(s.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Stock" : "Add Stock"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Design Name *</Label>
                <Input data-testid="stock-design-input" value={form.design_name} onChange={(e) => setForm({ ...form, design_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="stock-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="stock-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Color</Label>
                <Input data-testid="stock-color-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Size</Label>
                <Input data-testid="stock-size-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Quantity</Label>
                <Input data-testid="stock-quantity-input" type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Cost Price ₹</Label>
                <Input data-testid="stock-cost-input" type="number" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Selling Price ₹</Label>
                <Input data-testid="stock-selling-input" type="number" min="0" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
              </div>
            </div>
            <Button data-testid="stock-save-button" className="w-full rounded-lg" disabled={!form.design_name.trim() || save.isPending} onClick={() => save.mutate()}>
              {editId ? "Update Stock" : "Save Stock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
